import * as React from 'react';

import { App, Button, Collapse, Empty, Pagination, Skeleton, Tag, Tooltip, Typography } from 'antd';

import Truncate from 'v2/components/Shared/TruncateText';
import { WebhookStatus, WebhookStatusAlias, WebhookTypeAlias } from 'v2/interfaces/webhook2';
import type { Webhook } from 'v2/interfaces/webhook2';

import { Icon } from '@eduzz/ui-antd-theme';

import { Actions } from './Actions';
import { Filter } from './Filter';
import { useWebhookContext } from '../context';
import { History } from '../History';
import WebhookTour from '../Tour';

const WEBHOOK_STATUS_COLOR: Record<string, 'success' | 'error' | 'processing' | 'default'> = {
  active: 'success',
  failing: 'error',
  paused: 'processing',
  inactive: 'default'
};

const ITEMS_PER_PAGE = 20;
const TOOLBAR_HEIGHT = 64;
const SCROLL_DELAY = 50;

export const List = () => {
  const { message } = App.useApp();
  const { loading, webhooks, filter, listWebhooks } = useWebhookContext();

  const [copiedUrl, setCopiedUrl] = React.useState<string>();
  const [page, setPage] = React.useState(1);
  const [expandedIds, setExpandedIds] = React.useState<string[]>([]);

  const showConfiguredProduct = (products: number[]) => {
    if (products.includes(-1)) {
      return ['Todos'];
    }

    const firstThree = products.slice(0, 3).map(String);

    if (firstThree.length < 2) {
      return firstThree;
    }

    firstThree.push(`+${products.length - 3}`);

    return firstThree;
  };

  const handleCopy = (e: React.SyntheticEvent, w: Webhook) => {
    e.stopPropagation();
    navigator.clipboard.writeText(w.url);
    setCopiedUrl(w.url);
    message.success('URL copiada');

    setTimeout(() => {
      setCopiedUrl(null);
    }, 2000);
  };

  const handleChangePage = (value: number) => {
    setPage(value);

    const filtersElement = document.getElementById('webhook-filters');

    if (!filtersElement) {
      return;
    }

    setTimeout(() => {
      window.scroll({
        top: filtersElement.getBoundingClientRect().top + window.scrollY - TOOLBAR_HEIGHT,
        behavior: 'smooth'
      });
    }, SCROLL_DELAY);
  };

  React.useEffect(() => {
    listWebhooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const urlToLowerCase = filter?.url?.toLowerCase();

  const filtered = React.useMemo(
    () =>
      webhooks
        ?.filter(w =>
          !urlToLowerCase
            ? true
            : w?.url?.toLowerCase().includes(urlToLowerCase) || w.description?.toLowerCase().includes(urlToLowerCase)
        )
        .filter(f => (filter?.status === 'all' ? true : f.state === filter?.status))
        .filter(g => (filter?.type === 'all' ? true : g.type === filter?.type)),
    [filter?.status, filter?.type, urlToLowerCase, webhooks]
  );

  const paginatedData = React.useMemo(
    () => filtered?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page]
  );

  if (loading) {
    return (
      <div className='flex flex-col gap-3'>
        <Filter />
        {[...Array(5)].map((_, n) => (
          <Skeleton key={n} active paragraph={{ rows: 2 }} />
        ))}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <Filter />

      {filtered?.length === 0 && <Empty description='Nenhum webhook encontrado' />}

      {paginatedData.map((w, index) => {
        const isExpanded = expandedIds.includes(w._id);

        return (
          <Collapse
            key={w._id}
            destroyOnHidden
            activeKey={isExpanded ? [w._id] : []}
            onChange={keys => {
              const nextKeys = (Array.isArray(keys) ? keys : [keys]).map(String);
              setExpandedIds(prev => {
                const withoutCurrent = prev.filter(id => id !== w._id);
                return nextKeys.includes(w._id) ? [...withoutCurrent, w._id] : withoutCurrent;
              });
            }}
            expandIcon={() => null}
            items={[
              {
                key: w._id,
                label: (
                  <div className='grid w-full grid-cols-1 items-center gap-3 lg:grid-cols-12'>
                    <div className='flex min-w-0 flex-col gap-2 lg:col-span-4'>
                      <Typography.Text strong className='wrap-break-word'>
                        {w.description}
                      </Typography.Text>

                      <div className='flex flex-wrap items-center gap-1'>
                        <Tag color={WEBHOOK_STATUS_COLOR[w.state] || 'default'}>{WebhookStatusAlias[w.state]}</Tag>
                        {w.state === WebhookStatus.failing && (
                          <Tooltip title='Sua URL retornou erro diversas vezes para um mesmo evento, por esse motivo, sua URL foi marcada como falhando e salvamos os eventos subsequentes em backup, corrija sua integração e os eventos serão reenviados.'>
                            <span className='inline-flex'>
                              <Icon.CircleAlert size={16} className='text-(--uizz-antd-color-error)' />
                            </span>
                          </Tooltip>
                        )}
                      </div>

                      <div className='flex flex-wrap items-center gap-1'>
                        <Typography.Text type='secondary' className='text-sm'>
                          Produto(s):
                        </Typography.Text>
                        {showConfiguredProduct(w.product_id).map(p => (
                          <Tag key={p}>{p}</Tag>
                        ))}
                      </div>
                    </div>

                    <div className='flex min-w-0 items-center gap-2 lg:col-span-5'>
                      <Typography.Text type='secondary' className='min-w-0'>
                        <Truncate count={48} final='...' title={w.url}>
                          {w.url}
                        </Truncate>
                      </Typography.Text>
                      <Tooltip title={copiedUrl === w.url ? 'Copiado!' : 'Clique para copiar'}>
                        <Button
                          type='text'
                          shape='circle'
                          aria-label='Copiar URL'
                          icon={<Icon.Copy size={16} />}
                          onClick={e => handleCopy(e, w)}
                        />
                      </Tooltip>
                    </div>

                    <div className='lg:col-span-2'>
                      <Typography.Text type='secondary'>
                        {WebhookTypeAlias[w.type as keyof typeof WebhookTypeAlias]}
                      </Typography.Text>
                    </div>

                    <div className='flex items-center justify-end gap-1 lg:col-span-1'>
                      <div onClick={e => e.stopPropagation()}>
                        <Actions webhook={w} />
                      </div>
                      <Button
                        type='text'
                        shape='circle'
                        id={index === 0 ? 'webhook-v2-history-action' : undefined}
                        aria-label={isExpanded ? 'Recolher histórico' : 'Expandir histórico'}
                        icon={<Icon.ChevronDown size={16} className={isExpanded ? 'rotate-180' : undefined} />}
                      />
                    </div>
                  </div>
                ),
                children: <History webhook={w} />
              }
            ]}
          />
        );
      })}

      {filtered?.length > ITEMS_PER_PAGE && (
        <div className='flex w-full justify-end'>
          <Pagination total={filtered?.length} pageSize={ITEMS_PER_PAGE} current={page} onChange={handleChangePage} />
        </div>
      )}

      <WebhookTour />
    </div>
  );
};
